import { AlertTriangle, Bot, ChevronRight, Inbox, Layers3, Target } from "lucide-react";
import { displayBusinessName } from "../lib/productNames";
import "./ProductWorkQueues.css";

function nameFor(business, key) {
  return displayBusinessName(key, business) || business?.name || key || "Unassigned";
}

function rank(item) {
  const urgency = Number(item?.urgency_score || 0);
  const risk = Number(item?.risk_score || 0);
  const bucket = item?.bucket === "NOW" ? 50 : item?.bucket === "NEXT" ? 20 : 0;
  const kind = item?.kind === "needs_you" ? 40 : item?.kind === "at_risk" ? 35 : item?.kind === "waiting_on" ? 15 : 0;
  return bucket + kind + urgency * 3 + risk * 2;
}

function summarize(items) {
  const sorted = [...items].sort((a, b) => rank(b) - rank(a));
  return {
    total: items.length,
    needsYou: items.filter((item) => item.kind === "needs_you").length,
    blocked: items.filter((item) => item.kind === "at_risk" || item.kind === "waiting_on").length,
    aiHandling: items.filter((item) => item.kind === "ai_can_handle").length,
    now: items.filter((item) => item.bucket === "NOW").length,
    nextItem: sorted.find((item) => item.bucket === "NOW") || sorted.find((item) => item.bucket === "NEXT") || sorted[0] || null,
  };
}

export default function ProductWorkQueues({ items = [], businesses = [], onOpenProduct, onOpenItem }) {
  const byKey = new Map();
  for (const item of items) {
    const key = item.business_key || "unassigned";
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(item);
  }

  const businessMap = new Map(businesses.map((business) => [business.key || business.slug, business]));
  const cards = [...byKey.entries()]
    .map(([key, rows]) => ({ key, rows, business: businessMap.get(key), summary: summarize(rows) }))
    .sort((a, b) => {
      if (a.key === "unassigned") return 1;
      if (b.key === "unassigned") return -1;
      return (b.summary.needsYou * 100 + b.summary.blocked * 50 + b.summary.now * 10 + b.summary.total)
        - (a.summary.needsYou * 100 + a.summary.blocked * 50 + a.summary.now * 10 + a.summary.total);
    });

  if (!cards.length) {
    return <div className="product-queue-empty"><Layers3 size={18} /><span>No queued product work yet.</span></div>;
  }

  return (
    <section className="product-queue-grid" aria-label="Product work queues">
      {cards.map(({ key, summary, business }) => {
        const title = nameFor(business, key);
        const unassigned = key === "unassigned";
        return (
          <article className={`product-queue-card${unassigned ? " unassigned" : ""}`} key={key}>
            <button className="product-queue-main" onClick={() => onOpenProduct?.(key)}>
              <div className="product-queue-head">
                <span className="product-queue-icon">{unassigned ? <Inbox size={18} /> : <Layers3 size={18} />}</span>
                <div>
                  <small>{unassigned ? "INBOX" : "PRODUCT"}</small>
                  <h3>{title}</h3>
                </div>
                <ChevronRight size={17} />
              </div>

              <div className="product-queue-stats">
                <span><b>{summary.needsYou}</b><small>Needs You</small></span>
                <span><b>{summary.blocked}</b><small>Blocked / Risk</small></span>
                <span><b>{summary.aiHandling}</b><small>AI Handling</small></span>
                <span><b>{summary.total}</b><small>Queued</small></span>
              </div>

              <div className="product-queue-next">
                <Target size={14} />
                <div><small>NEXT</small><b>{summary.nextItem?.title || summary.nextItem?.name || "No immediate item"}</b></div>
              </div>
            </button>

            {summary.nextItem ? (
              <button className="product-queue-open-item" onClick={() => onOpenItem?.(summary.nextItem)}>
                {summary.nextItem.kind === "ai_can_handle" ? <Bot size={14} /> : summary.nextItem.kind === "at_risk" ? <AlertTriangle size={14} /> : <Target size={14} />}
                Open priority item
              </button>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}
